import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  Clock,
  Mail,
  MessageCircle,
  Mic,
  Send,
} from "lucide-react";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Reveal } from "@/components/motion/Reveal";
import { StaggerGrid } from "@/components/motion/StaggerGrid";

type InquiryId = "support" | "sales" | "partnership" | "press" | "other";
const INQUIRY_TO_CATEGORY: Record<InquiryId, "Support" | "Billing" | "General"> = {
  support: "Support",
  sales: "Billing",
  partnership: "General",
  press: "General",
  other: "General",
};

const contactSchema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  company: z.string().trim().max(120).optional().or(z.literal("")),
  inquiry: z.enum(["support", "sales", "partnership", "press", "other"]),
  message: z.string().trim().min(10).max(2000),
});

type ChannelKey = "support" | "sales" | "press";
const CHANNEL_TO_INQUIRY: Record<ChannelKey, InquiryId> = {
  support: "support",
  sales: "sales",
  press: "press",
};

export default function Contact() {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState<{
    name: string;
    email: string;
    company: string;
    inquiry: InquiryId;
    message: string;
  }>({ name: "", email: "", company: "", inquiry: "support", message: "" });

  const formRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  function pickChannel(key: ChannelKey) {
    setForm((f) => ({ ...f, inquiry: CHANNEL_TO_INQUIRY[key] }));
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => messageRef.current?.focus({ preventScroll: true }), 450);
    });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSuccess(null);
    const parsed = contactSchema.safeParse(form);
    if (!parsed.success) {
      toast({
        title: t("marketing.contact.errorTitle"),
        description: t("marketing.contact.errorBody"),
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    const inquiryLabel = t(`marketing.contact.inquiry.${parsed.data.inquiry}`);
    const subject = `[${inquiryLabel}] ${parsed.data.name}`;
    const fullBody = [
      `From: ${parsed.data.name} <${parsed.data.email}>`,
      parsed.data.company ? `Company: ${parsed.data.company}` : null,
      `Inquiry: ${inquiryLabel}`,
      "",
      parsed.data.message,
    ]
      .filter(Boolean)
      .join("\n");
    const { data, error } = await supabase.rpc("submit_support_ticket", {
      _category: INQUIRY_TO_CATEGORY[parsed.data.inquiry],
      _priority: "P3",
      _subject: subject,
      _body: fullBody,
      _email: parsed.data.email,
    });
    setSubmitting(false);
    if (error || !data) {
      toast({
        title: t("marketing.contact.errorTitle"),
        description: error?.message ?? t("marketing.contact.errorBody"),
        variant: "destructive",
      });
      return;
    }
    setSuccess(t("marketing.contact.successInline"));
    toast({
      title: t("marketing.contact.successTitle"),
      description: t("marketing.contact.successBody", { id: data }),
    });
    setForm({ name: "", email: "", company: "", inquiry: "support", message: "" });
  }

  const channels: {
    key: ChannelKey;
    Icon: typeof MessageCircle;
  }[] = [
    { key: "support", Icon: MessageCircle },
    { key: "sales", Icon: Briefcase },
    { key: "press", Icon: Mic },
  ];

  const socials = [
    { label: "LinkedIn", href: "#" },
    { label: "Twitter / X", href: "#" },
    { label: "Instagram", href: "#" },
    { label: "YouTube", href: "#" },
  ];

  return (
    <PublicShell>
      <Seo
        title={t("marketing.contact.seoTitle")}
        description={t("marketing.contact.seoDescription")}
      />

      {/* Quick channels */}
      <Reveal as="section" className="container py-12 md:py-16">
        <div className="mx-auto max-w-2xl text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
            {t("marketing.contact.eyebrow")}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            {t("marketing.contact.heroTitle")}
          </h2>
          <p className="mt-3 text-muted-foreground">
            {t("marketing.contact.heroSub")}
          </p>
        </div>

        <StaggerGrid className="grid gap-5 md:grid-cols-3">
          {channels.map(({ key, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => pickChannel(key)}
              className="group text-left"
            >
              <Card className="h-full p-6 transition-all hover:border-primary/50 hover:shadow-md">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {t(`marketing.contact.channels.${key}.response`)}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1.5">
                  {t(`marketing.contact.channels.${key}.title`)}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  {t(`marketing.contact.channels.${key}.desc`)}
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary group-hover:gap-2 transition-all">
                  {t(`marketing.contact.channels.${key}.action`)}
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Card>
            </button>
          ))}
        </StaggerGrid>
      </Reveal>

      {/* Form + sidebar */}
      <section ref={formRef} className="container pb-16 md:pb-24">
        <Reveal className="grid gap-8 lg:grid-cols-[1fr_360px]">
          {/* Form card */}
          <Card className="p-6 md:p-8">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-foreground">
                {t("marketing.contact.formTitle")}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("marketing.contact.formSub")}
              </p>
            </div>

            {success && (
              <div className="mb-6 flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
                <span className="text-foreground">{success}</span>
              </div>
            )}

            <form onSubmit={onSubmit} className="grid gap-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="c-name">{t("marketing.contact.fieldName")}</Label>
                  <Input
                    id="c-name"
                    required
                    maxLength={100}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="c-email">{t("marketing.contact.fieldEmail")}</Label>
                  <Input
                    id="c-email"
                    type="email"
                    required
                    maxLength={255}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="c-company">
                  {t("marketing.contact.fieldCompany")}{" "}
                  <span className="font-normal text-muted-foreground">
                    {t("marketing.contact.companyOptional")}
                  </span>
                </Label>
                <Input
                  id="c-company"
                  maxLength={120}
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="c-inquiry">{t("marketing.contact.inquiry.label")}</Label>
                <Select
                  value={form.inquiry}
                  onValueChange={(v) => setForm({ ...form, inquiry: v as InquiryId })}
                >
                  <SelectTrigger id="c-inquiry">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(["support", "sales", "partnership", "press", "other"] as InquiryId[]).map(
                      (id) => (
                        <SelectItem key={id} value={id}>
                          {t(`marketing.contact.inquiry.${id}`)}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="c-msg">{t("marketing.contact.fieldMessage")}</Label>
                <Textarea
                  ref={messageRef}
                  id="c-msg"
                  required
                  minLength={10}
                  maxLength={2000}
                  rows={6}
                  placeholder={t("marketing.contact.messagePlaceholder")}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>

              <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 pt-2">
                <p className="text-xs text-muted-foreground sm:max-w-[60%]">
                  {t("marketing.contact.trust")}
                </p>
                <Button type="submit" loading={submitting} size="lg" variant="brand" className="gap-2">
                  {submitting ? (
                    t("marketing.contact.submitting")
                  ) : (
                    <>
                      {t("marketing.contact.submit")}
                      <Send className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Card>

          {/* Sidebar */}
          <aside className="grid gap-5 content-start">
            <Card className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-primary" />
                <h3 className="text-base font-semibold text-foreground">
                  {t("marketing.contact.emailTitle")}
                </h3>
              </div>
              <a
                href={`mailto:${t("marketing.contact.emailValue")}`}
                className="block font-mono text-sm text-primary hover:underline mb-2 break-all"
              >
                {t("marketing.contact.emailValue")}
              </a>
              <p className="text-xs text-muted-foreground">
                {t("marketing.contact.emailNote")}
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-base font-semibold text-foreground mb-4">
                {t("marketing.contact.socialsTitle")}
              </h3>
              <ul className="space-y-2">
                {socials.map((s) => (
                  <li key={s.label}>
                    <a
                      href={s.href}
                      className="flex items-center justify-between rounded-md px-2 py-2 text-sm text-foreground hover:bg-muted transition-colors group"
                    >
                      <span>{s.label}</span>
                      <ArrowRight className="h-3.5 w-3.5 -rotate-45 text-muted-foreground group-hover:text-primary transition-colors" />
                    </a>
                  </li>
                ))}
              </ul>
            </Card>
          </aside>
        </Reveal>
      </section>
    </PublicShell>
  );
}